import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import LeaveClashDialog from './LeaveClashDialog';

const STATUS_STYLES = {
  approved: 'bg-green-500/20 text-green-400 border-green-500/30',
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
};

export default function LeaveRequestsTab({ api, formatDateDisplay }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [clashDialog, setClashDialog] = useState({ open: false, clashes: [] });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      const res = await api.get(`/worker-availability?${params}`);
      setEntries(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [api, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (entry) => {
    try {
      const res = await api.post(`/worker-availability/${entry.id}/approve`);
      toast.success(`Leave approved for ${entry.worker_name}`);
      const clashes = res.data?.clashing_allocations;
      if (clashes?.length > 0) {
        setClashDialog({ open: true, clashes });
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!rejectDialog) return;
    try {
      await api.post(`/worker-availability/${rejectDialog.id}/reject`, { rejection_reason: rejectionReason });
      toast.success(`Leave rejected for ${rejectDialog.worker_name}`);
      setRejectDialog(null);
      setRejectionReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  if (loading) return <div className="animate-pulse h-48 bg-muted rounded" />;

  const pendingCount = entries.filter(e => e.status === 'pending').length;

  return (
    <div className="space-y-4" data-testid="leave-requests-tab">
      {/* Filters */}
      <div className="flex items-center gap-3">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]" data-testid="lr-filter-status">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Requests</SelectItem>
            <SelectItem value="pending">Pending ({pendingCount})</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="card-industrial">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="label-overline">Worker</TableHead>
                <TableHead className="label-overline">Leave Type</TableHead>
                <TableHead className="label-overline">From</TableHead>
                <TableHead className="label-overline">To</TableHead>
                <TableHead className="label-overline">Reason</TableHead>
                <TableHead className="label-overline">Status</TableHead>
                <TableHead className="label-overline text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map(entry => (
                <TableRow key={entry.id} data-testid={`lr-row-${entry.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <User className="h-3.5 w-3.5 text-muted-foreground" />
                      {entry.worker_name || 'Unknown'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: entry.leave_type_color || '#71717a' }} />
                      <span className="text-sm">{entry.leave_type_name || 'Unknown'}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{formatDateDisplay ? formatDateDisplay(entry.start_date) : entry.start_date}</TableCell>
                  <TableCell className="font-mono text-sm">{formatDateDisplay ? formatDateDisplay(entry.end_date) : entry.end_date}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{entry.reason || '-'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_STYLES[entry.status] || ''}>
                      {entry.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {entry.status === 'pending' ? (
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleApprove(entry)}
                          className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                          data-testid={`approve-${entry.id}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setRejectDialog(entry); setRejectionReason(''); }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`reject-${entry.id}`}
                        >
                          <XCircle className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </div>
                    ) : entry.status === 'rejected' && entry.rejection_reason ? (
                      <span className="text-xs text-muted-foreground italic" title={entry.rejection_reason}>
                        {entry.rejection_reason.length > 30 ? entry.rejection_reason.slice(0, 30) + '...' : entry.rejection_reason}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    No leave requests found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={(open) => { if (!open) setRejectDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reject Leave Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Rejecting leave for <strong className="text-foreground">{rejectDialog?.worker_name}</strong> ({rejectDialog?.leave_type_name})
            </p>
            <div className="space-y-2">
              <Label className="label-overline">Reason (optional)</Label>
              <Textarea value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} placeholder="Reason for rejection..." rows={2} data-testid="rejection-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} data-testid="confirm-reject-btn">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LeaveClashDialog
        open={clashDialog.open}
        clashes={clashDialog.clashes}
        onClose={() => setClashDialog({ open: false, clashes: [] })}
      />
    </div>
  );
}
